/**
 * Analytics tracking utility for Google Analytics 4 (GA4)
 * 
 * This module provides a consistent interface for tracking events
 * across the application, specifically for subscription page analytics.
 */

declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
  }
}

/**
 * Track a custom event in Google Analytics
 * 
 * @param eventName - The name of the event (e.g., 'subscription_page_view')
 * @param eventParams - Additional parameters for the event
 */
export function trackEvent(eventName: string, eventParams?: Record<string, any>): void {
  // Ensure dataLayer exists
  if (typeof window === 'undefined' || !window.dataLayer) {
    console.warn('[Analytics] dataLayer not initialized, event not tracked:', eventName);
    return;
  }

  // Build the event payload
  const payload = {
    event: eventName,
    ...(eventParams || {}),
  };

  // Always push to dataLayer for GTM-style triggers and manual inspection
  // This ensures events are visible to downstream tools and tests
  window.dataLayer.push(payload);

  // Also use gtag format for GA4 if available (gtag.js expects this format)
  // This ensures events are properly consumed by GA4
  if (window.gtag) {
    window.gtag('event', eventName, eventParams || {});
  }
}

/**
 * Track subscription page view
 */
export function trackSubscriptionPageView(): void {
  trackEvent('subscription_page_view', {
    page_title: 'Subscription Page',
    page_location: window.location.href,
  });
}

/**
 * Track annual/monthly toggle usage
 * 
 * @param isAnnual - Whether annual billing is selected
 */
export function trackSubscriptionToggle(isAnnual: boolean): void {
  trackEvent('subscription_toggle_annual', {
    billing_period: isAnnual ? 'annual' : 'monthly',
  });
}

/**
 * Track pricing card click/interaction
 * 
 * @param plan - The plan that was clicked
 * @param isAnnual - Whether annual billing is selected
 * @param action - The action taken (e.g., 'view', 'click')
 */
export function trackPricingCardInteraction(
  plan: string,
  isAnnual: boolean,
  action: 'view' | 'click' = 'click'
): void {
  trackEvent('subscription_pricing_card_interaction', {
    plan_name: plan,
    billing_period: isAnnual ? 'annual' : 'monthly',
    action,
  });
}

/**
 * Track plan selection (Get Started button click)
 * 
 * @param plan - The selected plan
 * @param isAnnual - Whether annual billing is selected
 */
export function trackPlanSelected(plan: string, isAnnual: boolean): void {
  trackEvent('subscription_plan_selected', {
    plan_name: plan,
    billing_period: isAnnual ? 'annual' : 'monthly',
  });
}

/**
 * Track checkout session start
 * 
 * @param plan - The plan being subscribed to
 * @param isAnnual - Whether annual billing is selected
 */
export function trackCheckoutStarted(plan: string, isAnnual: boolean): void {
  trackEvent('subscription_checkout_started', {
    plan_name: plan,
    billing_period: isAnnual ? 'annual' : 'monthly',
    value: isAnnual ? 'annual_price' : 'monthly_price', // Can be enhanced with actual price
  });
}

/**
 * Track successful subscription completion
 * 
 * @param plan - The subscribed plan
 * @param isAnnual - Whether annual billing is selected
 */
export function trackCheckoutCompleted(plan: string, isAnnual: boolean): void {
  trackEvent('subscription_checkout_completed', {
    plan_name: plan,
    billing_period: isAnnual ? 'annual' : 'monthly',
  });
}

/**
 * Track FAQ item expansion
 * 
 * @param question - The FAQ question that was expanded
 */
export function trackFAQExpanded(question: string): void {
  trackEvent('subscription_faq_expanded', {
    question: question.substring(0, 100), // Limit length
  });
}

/**
 * Track features comparison view
 */
export function trackFeaturesViewed(): void {
  trackEvent('subscription_features_viewed');
}

/**
 * Track trip examples interaction
 * 
 * @param action - The action taken (e.g., 'view', 'calculate_click')
 */
export function trackTripExamplesInteraction(action: 'view' | 'calculate_click'): void {
  trackEvent('subscription_trip_examples_interaction', {
    action,
  });
}

/**
 * Track savings calculator interaction
 * 
 * @param action - The action taken (e.g., 'view', 'slider_change', 'subscribe')
 * @param annualSpending - The annual spending value (if applicable)
 * @param plan - The plan selected (if applicable)
 */
export function trackSavingsCalculatorInteraction(
  action: 'view' | 'slider_change' | 'subscribe',
  annualSpending?: number,
  plan?: string
): void {
  trackEvent('subscription_savings_calculator_interaction', {
    action,
    ...(annualSpending !== undefined && { annual_spending: annualSpending }),
    ...(plan && { plan_name: plan }),
  });
}

/**
 * Track social proof engagement
 * 
 * @param action - The action taken (e.g., 'view', 'click')
 */
export function trackSocialProofEngagement(action: 'view' | 'click' = 'view'): void {
  trackEvent('subscription_social_proof_engagement', {
    action,
  });
}

